import React, { useEffect, useState } from 'react';
import Icon from '@mdi/react';
import { mdiMagnify, mdiMessageTextOutline } from '@mdi/js';
import ApiCall from '@/lib/apiCall';
import { useDebounce } from '@/lib/useDebounce';
import { useAppliance } from '@/lib/useAppliance';
import TextoData from '@/components/tipografia/TextoData';
import Skeleton from '@/components/layout/Skeleton';
import type { IaConversaResumoDTO } from '@webip/shared/src/dto/ia';
import styles from './HistoricoConversas.module.css';

/**
 * Lista + busca das conversas do operador com o assistente.
 *
 * A busca roda no servidor (título e conteúdo das mensagens — ver
 * `RepoIaConversa.buscar`), então o debounce aqui só evita uma requisição por
 * tecla digitada.
 */

type Props = {
    /** Conversa aberta no painel agora, para destacar na lista. */
    conversaAtualId?: number;
    onSelecionar: (id: number) => void;
};

const STATUS_ROTULO: Record<string, string> = {
    aberta: 'Em andamento',
    aplicada: 'Aplicada',
    descartada: 'Descartada',
};

const HistoricoConversas = ({ conversaAtualId, onSelecionar }: Props) => {
    const { appliances } = useAppliance();
    const [termo, setTermo] = useState('');
    const termoBuscado = useDebounce(termo.trim(), 350);

    const [lista, setLista] = useState<IaConversaResumoDTO[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        let cancelado = false;
        setCarregando(true);
        setErro(null);

        ApiCall.get('/api/ia/conversa', termoBuscado ? { q: termoBuscado } : {})
            .then((res) => {
                if (cancelado) return;
                setLista(res?.result ?? []);
            })
            .catch(() => {
                if (!cancelado) setErro('Não foi possível carregar o histórico.');
            })
            .finally(() => {
                if (!cancelado) setCarregando(false);
            });

        return () => {
            cancelado = true;
        };
    }, [termoBuscado]);

    const nomeAppliance = (id: number | null) => {
        if (id == null) return null;
        return appliances.find((a) => a.id === id)?.nome ?? `appliance #${id}`;
    };

    return (
        <div className={styles.historico}>
            <div className={styles.busca}>
                <Icon path={mdiMagnify} size={0.8} />
                <input
                    type="text"
                    placeholder="Buscar por título ou conteúdo da conversa..."
                    value={termo}
                    onChange={(e) => setTermo(e.target.value)}
                    autoFocus
                />
            </div>

            {erro && <div className={styles.estado}>{erro}</div>}

            {carregando && (
                <div className={styles.carregando}>
                    <Skeleton format="text" />
                    <Skeleton format="text" />
                    <Skeleton format="text" />
                </div>
            )}

            {!erro && !carregando && lista.length === 0 && (
                <div className={styles.estado}>
                    {termoBuscado ? 'Nenhuma conversa encontrada.' : 'Você ainda não tem conversas.'}
                </div>
            )}

            <ul className={styles.lista}>
                {!carregando && lista.map((c) => (
                    <li key={c.id}>
                        <button
                            type="button"
                            className={[styles.item, c.id === conversaAtualId ? styles.itemAtivo : ''].join(' ')}
                            onClick={() => onSelecionar(c.id)}
                        >
                            <Icon path={mdiMessageTextOutline} size={0.75} className={styles.itemIcone} />
                            <div className={styles.itemTexto}>
                                <span className={styles.itemTitulo}>{c.titulo || 'Conversa sem título'}</span>
                                <span className={styles.itemMeta}>
                                    {nomeAppliance(c.appliance_id) ?? 'Sem appliance'}
                                    {' · '}
                                    {STATUS_ROTULO[c.status] ?? c.status}
                                    {c.data_ultima_atualizacao && (
                                        <>
                                            {' · '}
                                            <TextoData value={c.data_ultima_atualizacao} tamanho="curto" />
                                        </>
                                    )}
                                </span>
                            </div>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default HistoricoConversas;
